import { useRef, useCallback } from "react";
import { Link } from "react-router-dom";

interface SpotlightCardProps {
  children: React.ReactNode;
  className?: string;
  link?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

/**
 * HUD card with mouse-tracking spotlight border glow.
 * Uses CSS custom properties + useRef for 60fps (no re-renders).
 */
export function SpotlightCard({ children, className = "", link, style, onClick }: SpotlightCardProps) {
  const ref = useRef<HTMLDivElement | HTMLAnchorElement>(null);

  const handleMove = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--spotlight-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--spotlight-y", `${e.clientY - rect.top}px`);
  }, []);

  const baseClass = `spotlight-card rounded-none border border-border bg-card p-3 transition-colors ${className}`;

  if (link) {
    return (
      <Link
        to={link}
        ref={ref as React.RefObject<HTMLAnchorElement>}
        className={`${baseClass} block hover:bg-[rgba(206,214,255,0.04)]`}
        style={style}
        onMouseMove={handleMove}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={baseClass}
      style={style}
      onMouseMove={handleMove}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/**
 * Shimmer skeleton block — directional light sweep.
 * Use instead of animate-pulse for premium loading states.
 */
export function ShimmerBlock({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={`skeleton-shimmer rounded-sm ${className}`} style={style} />;
}

/**
 * HUD loading bar — thin teal sweep indicator.
 * Use at the top of content areas during data fetching.
 */
export function HudLoader() {
  return <div className="hud-loader" />;
}
